'use client'

import Link from 'next/link'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { ProductHero, Statement, NextSteps, ProductCta, RelatedCases } from '@/components/platform/v2/ProductKit'
import { IntegrationBridge } from '@/components/platform/v2/Mocks'
import { LIVE_INTEGRATIONS } from '@/data/integrations'

const IntegrationsPage = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <ProductHero
                eyebrow="Интеграции"
                titleLines={['Не заставляем менять всё.', 'Соединяем то, что уже работает.']}
                lead="VCENTRE.ONE может встраиваться в существующий IT-контур бизнеса — CRM, 1С, платежи, данные и внешние сервисы."
                primary={{ label: 'Проверить мои интеграции', to: '/signin?focus=integrations' }}
                secondary={{ label: 'Обзор платформы', to: '/platform' }}
                visual={
                    <img
                        src={'/assets/images/platform/integrations-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT соединяет кабели с фразой «Интеграции в деле»"
                        width={1254}
                        height={1254}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            <Section tone="quiet">
                <SectionHeading
                    align="left"
                    eyebrow="Ключевая идея"
                    title="Интеграция — это не логотип, а сценарий"
                    text="Важно не «мы дружим с 1С», а что именно перестаёт делаться руками."
                />
                <div className="mt-8 grid gap-4 lg:grid-cols-3">
                    <IntegrationBridge
                        external="1С"
                        items={['Номенклатура', 'Цены', 'Остатки', 'Заказы']}
                        note="Клиент видит в приложении актуальный каталог."
                    />
                    <IntegrationBridge
                        external="Платежи"
                        items={['Счёт', 'Оплата', 'Статус', 'Документ']}
                        note="Менеджер не сверяет платежи вручную."
                    />
                    <IntegrationBridge
                        external="Внешняя CRM"
                        items={['Клиенты', 'Заявки', 'Ответственные']}
                        note="Компания продолжает работать в привычной системе."
                    />
                </div>
            </Section>

            <Section>
                <SectionHeading
                    align="left"
                    eyebrow="Работает сейчас"
                    title="Подтверждённые интеграции"
                    text="Эти сценарии уже используются в проектах на платформе."
                />
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {LIVE_INTEGRATIONS.map((i) => (
                        <article key={i.id} className="surface surface-hover flex flex-col items-center gap-3 text-center">
                            <h3 className="text-base font-bold text-foreground">{i.name}</h3>
                            <p className="body-text">{i.description}</p>
                        </article>
                    ))}
                </div>
            </Section>

            <Section>
                <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-center">
                    <Statement>
                        Нет нужного сервиса в списке?
                        <br />
                        <span className="text-primary">Разберём вашу задачу отдельно.</span>
                    </Statement>
                    <div className="surface-quiet flex flex-col gap-3">
                        <p className="body-text">
                            Если у внешней системы есть подходящий API или другой поддерживаемый способ обмена, можем отдельно оценить
                            интеграцию под ваш процесс.
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Мы не обещаем автоматическую интеграцию с любым сервисом: сначала смотрим, что технически доступно.
                        </p>
                    </div>
                </div>
                <div className="mt-6 text-center">
                    <Link href="/signin?focus=integrations&source=missing-integration" className="btn-link">
                        Показать мой текущий контур
                    </Link>
                </div>
            </Section>

            <Section tone="quiet">
                <RelatedCases
                    slugs={['piter', 'private-clinic']}
                    text="Проекты, где платформа работает вместе с 1С, платежами и внешними системами."
                />
            </Section>

            <Section>
                <NextSteps
                    items={[
                        { to: '/platform/modules', text: 'Нужен отдельный сценарий под задачу бизнеса' },
                        { to: '/platform/automation-ai', text: 'Свяжите внешние данные со сценариями' },
                    ]}
                />
            </Section>

            <Section tone="quiet">
                <ProductCta
                    title="Покажите, как сейчас работает ваш бизнес"
                    text="Мы разберём, что можно связать с VCENTRE.ONE, а где потребуется проектная работа."
                    buttonLabel="Разобрать мои интеграции"
                    to="/signin?focus=integrations&source=integrations-bottom"
                    secondary={{ to: '/platform', label: 'Обзор платформы' }}
                    visual={
                        <img
                            src={'/assets/images/platform/integrations-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={1254}
                            height={1254}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default IntegrationsPage
